import { Command } from 'commander';
import { promises as fs } from 'fs';
import { join } from 'path';
import { select, input, checkbox } from '@inquirer/prompts';
import { execa } from 'execa';
import { ConfigService } from '../../services/config.service.js';
import { StarterKitService } from '../../services/starter-kit.service.js';
import { HookExecutor } from '../../services/hook-executor.service.js';
import { ComponentsService } from '../../services/components.service.js';
import { ValidationError } from '../../errors/index.js';
import { selectTheme, checkboxTheme } from '../../utils/prompt-theme.js';

export function createKitCreateCommand(): Command {
    return new Command('create')
        .description('Create a new project from a starter kit')
        .argument('<slug>', 'Starter kit slug')
        .argument('[directory]', 'Project directory (default: ./<slug>)')
        .option('-y, --yes', 'Auto mode - use defaults, no prompts')
        .option('--name <name>', 'Project name')
        .option('--skip-install', 'Skip dependency installation')
        .option('--skip-git', 'Skip git initialization')
        .option('--skip-framework', 'Skip framework apply')
        .option('--skip-ide', 'Skip IDE sync')
        .action(async (slug, directory, options) => {
            const configService = new ConfigService();
            const config = await configService.load();

            if (!config) {
                throw new ValidationError('Not initialized. Run "jai1 auth" first.');
            }

            const isAutoMode = options.yes === true;
            const kitService = new StarterKitService();

            // Step 1: Fetch metadata
            console.log(`📦 Fetching starter kit: ${slug}...`);
            const kit = await kitService.get(config, slug);

            // Step 2: Check required tools
            if (kit.config.requiredTools && kit.config.requiredTools.length > 0) {
                console.log('🔍 Checking required tools...');
                for (const tool of kit.config.requiredTools) {
                    try {
                        await execa('which', [tool]);
                    } catch {
                        console.warn(`⚠️  Warning: Required tool '${tool}' not found`);
                    }
                }
            }

            // Determine target directory
            const targetDir = directory || join(process.cwd(), options.name || slug);

            // Check if directory exists
            try {
                await fs.access(targetDir);
                throw new Error(`Directory already exists: ${targetDir}`);
            } catch (error) {
                if ((error as NodeJS.ErrnoException).code !== 'ENOENT') {
                    throw error;
                }
            }

            // Step 3: Run preInit hooks
            if (kit.config.hooks?.preInit && kit.config.hooks.preInit.length > 0) {
                console.log('🔧 Running pre-init hooks...');
                const hookExecutor = new HookExecutor(process.cwd(), {});
                await hookExecutor.run(kit.config.hooks.preInit);
            }

            // Step 4: Download and extract
            console.log('📥 Downloading template...');
            await kitService.downloadAndExtract(config, slug, targetDir, (percent) => {
                if (percent === 100) {
                    console.log('   ✓ Downloaded and extracted');
                }
            });

            // Step 5: Run postExtract hooks
            if (kit.config.hooks?.postExtract && kit.config.hooks.postExtract.length > 0) {
                console.log('📄 Setting up environment...');
                const hookExecutor = new HookExecutor(targetDir, {});
                await hookExecutor.run(kit.config.hooks.postExtract);
            }

            // Step 6: Prompt variables or use defaults
            const variables: Record<string, string> = {};

            if (kit.config.variables && kit.config.variables.length > 0) {
                // Filter out PROJECT_SCOPE — it will be auto-derived from PROJECT_NAME
                const promptVariables = kit.config.variables.filter(
                    (v) => v.name !== 'PROJECT_SCOPE'
                );

                if (isAutoMode) {
                    console.log('📝 Using default values (auto mode)');
                    for (const v of promptVariables) {
                        variables[v.name] =
                            options.name && v.name === 'PROJECT_NAME'
                                ? options.name
                                : v.default || '';
                    }
                } else {
                    console.log('📝 Please provide project details:');
                    for (const v of promptVariables) {
                        if (v.type === 'select' && v.options) {
                            const answer = await select({
                                message: v.prompt,
                                choices: v.options.map((opt) => ({ name: opt, value: opt })),
                                default: v.default,
                                theme: selectTheme,
                            });
                            variables[v.name] = answer as string;
                        } else {
                            const answer = await input({
                                message: v.prompt,
                                default: v.default,
                            });
                            variables[v.name] = answer;
                        }
                    }
                }

                // Auto-derive PROJECT_SCOPE from PROJECT_NAME if the kit defines it
                const hasScopeVar = kit.config.variables.some(
                    (v) => v.name === 'PROJECT_SCOPE'
                );
                if (hasScopeVar && variables['PROJECT_NAME']) {
                    variables['PROJECT_SCOPE'] = `@${variables['PROJECT_NAME']}`;
                }
            }

            // Step 7: Apply variable substitution
            if (Object.keys(variables).length > 0) {
                await applyVariableSubstitution(targetDir, variables);
            }

            // Step 8: Apply framework components
            if (!options.skipFramework && kit.config.framework?.apply) {
                console.log('🤖 Applying jai1 framework...');
                const componentsService = new ComponentsService(targetDir);

                // Expand paths with special prefixes (e.g., package:core)
                const expandedPaths = await componentsService.expandPaths(
                    config,
                    kit.config.framework.components
                );

                for (const filepath of expandedPaths) {
                    console.log(`   📥 Installing ${filepath}...`);
                    await componentsService.install(config, filepath, join(targetDir, '.jai1'));
                }

                console.log('   ✓ Framework components applied');
            }

            // Step 9: Git init
            if (!options.skipGit && kit.config.git?.init) {
                console.log('🔧 Initializing git repository...');
                await execa('git', ['init'], { cwd: targetDir });
                await execa('git', ['add', '.'], { cwd: targetDir });
                const commitMsg = kit.config.git.initialCommit || 'Initial commit';
                await execa('git', ['commit', '-m', commitMsg], { cwd: targetDir });
                console.log('   ✓ Git initialized');
            }

            // Step 10: Run postInit hooks
            if (!options.skipInstall && kit.config.hooks?.postInit) {
                console.log('🔧 Running setup...');
                const hookExecutor = new HookExecutor(targetDir, variables);
                await hookExecutor.run(kit.config.hooks.postInit);
            }

            // Step 11: IDE sync
            const shouldPromptIdeSync = kit.config.ide?.promptSync !== false;
            if (!options.skipIde && shouldPromptIdeSync) {
                const ideChoices = [
                    { name: 'Cursor', value: 'cursor' },
                    { name: 'Windsurf', value: 'windsurf' },
                    { name: 'Claude Code', value: 'claude' },
                    { name: 'Antigravity', value: 'antigravity' },
                    { name: 'OpenCode', value: 'opencode' },
                    { name: 'Codex', value: 'codex' },
                ];

                let selectedIdes: string[] = [];

                if (isAutoMode) {
                    selectedIdes = kit.config.ide?.defaultTargets || [];
                    if (selectedIdes.length > 0) {
                        console.log(`🖥️  Syncing to IDEs: ${selectedIdes.join(', ')}`);
                    }
                } else {
                    const defaultTargets = kit.config.ide?.defaultTargets || [];
                    const answer = await checkbox({
                        message: 'Chọn IDE để sync framework:',
                        choices: ideChoices.map((c) => ({
                            ...c,
                            checked: defaultTargets.includes(c.value),
                        })),
                        theme: checkboxTheme,
                    });
                    selectedIdes = answer as string[];
                }

                if (selectedIdes.length > 0) {
                    await execa('jai1', ['ide', 'sync', '--ides', ...selectedIdes, '--type', 'rules', 'workflows', 'commands'], {
                        cwd: targetDir,
                        stdio: 'inherit',
                    });
                }
            }

            // Done
            console.log('\n✅ Project created successfully!');
            console.log(`📁 Location: ${targetDir}`);
            console.log('\n💡 Next steps:');
            console.log(`   cd ${targetDir}`);
            console.log('   jai1 apply              # Install framework components');
            console.log('   jai1 ide sync <ides>    # Sync to your IDE(s)');
        });
}

/**
 * Apply variable substitution to files
 */
async function applyVariableSubstitution(
    dir: string,
    variables: Record<string, string>
): Promise<void> {
    const files = await getAllFiles(dir);

    for (const file of files) {
        let content = await fs.readFile(file, 'utf-8');
        let modified = false;

        for (const [key, value] of Object.entries(variables)) {
            const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
            if (regex.test(content)) {
                content = content.replace(regex, value);
                modified = true;
            }
        }

        if (modified) {
            await fs.writeFile(file, content, 'utf-8');
        }
    }
}

/**
 * Get all files recursively
 */
async function getAllFiles(dir: string): Promise<string[]> {
    const files: string[] = [];
    const entries = await fs.readdir(dir, { withFileTypes: true });

    for (const entry of entries) {
        const fullPath = join(dir, entry.name);
        if (entry.isDirectory()) {
            if (!entry.name.startsWith('.') && entry.name !== 'node_modules') {
                files.push(...(await getAllFiles(fullPath)));
            }
        } else {
            files.push(fullPath);
        }
    }

    return files;
}
